import { auth0ApiClient } from './auth0ApiClient'
import {
  Task,
  CreateTaskRequest,
  CreateGroupTaskRequest,
  UpdateTaskStatusRequest,
  UpdateGroupTaskRequest,
  GroupTaskUpdateResponse,
  GroupTaskDeleteResponse,
  TaskSubmission,
  TaskFeedback,
  TaskStats
} from '@/types/task'

interface TaskSearchParams {
  status?: string | string[]
  type?: string | string[]
  priority?: string | string[]
  tags?: string | string[]
  dueDateFrom?: string
  dueDateTo?: string
  createdFrom?: string
  createdTo?: string
  progressMin?: number
  progressMax?: number
  search?: string
  page?: number
  limit?: number
}

// Note: auth0ApiClient.get() extracts response.data.data, so we receive Task[] directly
type TaskSearchResponse = Task[]

interface TaskSubmissionRequest {
  content: string
  attachments?: File[]
}

interface TaskFeedbackRequest {
  rating: number
  comment: string
  suggestions?: string[]
}

class TaskService {
  private readonly baseUrl = '/tasks'

  /**
   * Create a new task (mentor only)
   */
  async createTask(data: CreateTaskRequest): Promise<Task> {
    return auth0ApiClient.post<Task>(`${this.baseUrl}/create`, data)
  }

  /**
   * Create one task per mentee in a single batch request, all sharing a groupId (mentor only)
   */
  async createGroupTask(data: CreateGroupTaskRequest): Promise<Task[]> {
    return auth0ApiClient.post<Task[]>(`${this.baseUrl}/create`, data)
  }

  /**
   * Get task statistics for current user
   */
  async getTaskStats(): Promise<TaskStats> {
    return auth0ApiClient.get<TaskStats>(`${this.baseUrl}/stats`)
  }

  /**
   * Get all tasks for the current user
   */
  async getMyTasks(params?: TaskSearchParams): Promise<TaskSearchResponse> {
    const queryString = params ? this.buildQueryString(params) : ''
    return auth0ApiClient.get<TaskSearchResponse>(`${this.baseUrl}/my-tasks${queryString}`)
  }

  /**
   * Get every sibling task in a group assignment (mentor only)
   */
  async getGroupTasks(groupId: string): Promise<Task[]> {
    return auth0ApiClient.get<Task[]>(`${this.baseUrl}/group/${groupId}`)
  }

  /**
   * Cascading edit: apply field updates to every eligible sibling task in a group (mentor only)
   */
  async updateGroupTask(groupId: string, data: UpdateGroupTaskRequest): Promise<GroupTaskUpdateResponse> {
    return auth0ApiClient.patch<GroupTaskUpdateResponse>(`${this.baseUrl}/group/${groupId}`, data)
  }

  /**
   * Cascading delete: soft-delete every eligible sibling task in a group (mentor only)
   */
  async deleteGroupTask(groupId: string): Promise<GroupTaskDeleteResponse> {
    return auth0ApiClient.delete<GroupTaskDeleteResponse>(`${this.baseUrl}/group/${groupId}`)
  }

  /**
   * Get tasks for a specific relationship
   */
  async getTasksForRelationship(
    relationshipId: string,
    params?: TaskSearchParams
  ): Promise<TaskSearchResponse> {
    const queryString = params ? this.buildQueryString(params) : ''
    return auth0ApiClient.get<TaskSearchResponse>(
      `${this.baseUrl}/relationship/${relationshipId}${queryString}`
    )
  }

  /**
   * Get specific task by ID
   */
  async getTask(taskId: string): Promise<Task> {
    return auth0ApiClient.get<Task>(`${this.baseUrl}/${taskId}`)
  }

  /**
   * Update task status (mentee only)
   */
  async updateTaskStatus(
    taskId: string,
    data: UpdateTaskStatusRequest
  ): Promise<Task> {
    return auth0ApiClient.put<Task>(`${this.baseUrl}/${taskId}/status`, data)
  }

  /**
   * Mark task as complete (mentee only)
   */
  async completeTask(taskId: string): Promise<Task> {
    return auth0ApiClient.put<Task>(`${this.baseUrl}/${taskId}/complete`, {})
  }

  /**
   * Submit task work with optional file attachments (mentee only)
   */
  async submitTaskWork(
    taskId: string,
    data: TaskSubmissionRequest
  ): Promise<TaskSubmission> {
    const formData = new FormData()
    formData.append('content', data.content)

    if (data.attachments && data.attachments.length > 0) {
      data.attachments.forEach((file) => {
        formData.append('attachments', file)
      })
    }

    return auth0ApiClient.upload<TaskSubmission>(
      `${this.baseUrl}/${taskId}/submit`,
      formData
    )
  }

  /**
   * Provide feedback on task submission (mentor only)
   */
  async provideFeedback(
    taskId: string,
    data: TaskFeedbackRequest
  ): Promise<TaskFeedback> {
    return auth0ApiClient.post<TaskFeedback>(`${this.baseUrl}/${taskId}/feedback`, data)
  }

  /**
   * Delete a task (mentor only)
   */
  async deleteTask(taskId: string): Promise<void> {
    return auth0ApiClient.delete<void>(`${this.baseUrl}/${taskId}`)
  }

  /**
   * Build query string from search parameters
   */
  private buildQueryString(params: TaskSearchParams): string {
    const searchParams = new URLSearchParams()

    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        if (Array.isArray(value)) {
          value.forEach((v) => searchParams.append(key, v.toString()))
        } else {
          searchParams.append(key, value.toString())
        }
      }
    })

    const queryString = searchParams.toString()
    return queryString ? `?${queryString}` : ''
  }
}

export const taskService = new TaskService()
